import { Tooltip } from '@radix-ui/themes';
import clsx from 'clsx';
import { Info } from 'lucide-react';
import type { ComponentProps } from 'react';
import { hasKey, EQuestionType } from '@hubertai/screening-question-types';
import { nanoid } from '@reduxjs/toolkit';
import type { TSortableContainerItem } from '.';
import { HubertButton } from 'components/HubertUI/HubertButton';
import HubertLabel from 'components/HubertUI/HubertLabel';
import {
  ECreateQuestionMode,
  useQuestionEditorContext,
} from 'components/Questionsv2/builder/context/create-question.context';
import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import {
  selectQuestionMode,
  selectQuestionLanguage,
  selectQuestionIsReadonly,
  selectWritesTemplateOverrides,
} from 'rtk/questionEditorSelector';
import { useTemplateId } from 'hooks/useTemplate';
import { customParametersUpdate } from 'rtk/TemplateSlice';
import type { TTemplateParameterKeys } from 'types/template/template.types';
import { EEventType } from 'types/events.enum';
import { addText, resetText } from 'rtk/Question.Editor.Slice';
import type { TextType } from 'rtk/question.text.reducer';
import UneditableInfoTooltip from 'components/Questionsv2/builder/UneditableInfoTooltip';
interface Props extends ComponentProps<'div'> {
  data: TSortableContainerItem;
}

const TextBubbleDragAndDropHeaderv2 = ({ data, ...props }: Props) => {
  const dispatch = useAppDispatch();
  const { questionId } = useQuestionEditorContext();
  const mode = useAppSelector((state) => selectQuestionMode(state, questionId));
  const questionLanguage = useAppSelector((state) => selectQuestionLanguage(state, questionId));
  const type = useAppSelector((state) => state.questionEditor.byId[questionId].type);
  const isPublishedOrNonExclusiveQuestion = useAppSelector(
    (state) => state.questionEditor.byId[questionId].isPublishedOrNonExclusiveQuestion,
  );
  const q = useAppSelector((state) => state.questionEditor.byId[questionId]);
  const templateId = useTemplateId();
  const writesTemplateOverrides = useAppSelector((state) => selectWritesTemplateOverrides(state, questionId));

  const isReadOnly = useAppSelector((state) => selectQuestionIsReadonly(state, questionId));
  const disabled =
    mode === ECreateQuestionMode.InTemplateEdit &&
    data &&
    data.id === 'text' &&
    type !== EQuestionType.Info &&
    (isPublishedOrNonExclusiveQuestion ?? true);

  return (
    <div {...props} className={clsx('hubert-vertical-center flex flex-row justify-between gap-2', 'w-full', 'px-2')}>
      <div className="hubert-vertical-center flex flex-row justify-start gap-2">
        <Tooltip content={isReadOnly ? 'Read only - edits not possible.' : 'Click text to edit. Shift+Enter to save.'}>
          <Info className="ml-1 text-gray-400" />
        </Tooltip>
        <HubertLabel className="text-sm font-medium" htmlFor="create-question-text">
          {data ? data.headerName : 'headerName'}
        </HubertLabel>
      </div>

      {!isReadOnly ? (
        <div className="hubert-vertical-center flex flex-row justify-start gap-2">
          {data && hasKey(data, 'isDefault') && data.isDefault === false ? (
            <UneditableInfoTooltip disabled={disabled}>
              <HubertButton
                size={'xs'}
                disabled={disabled}
                variant={'default'}
                onClick={() => {
                  const isDefault = data.isDefault;
                  console.log(data);
                  dispatch(resetText({ questionId, textType: data.id as TextType }));

                  if (templateId && writesTemplateOverrides) {
                    dispatch(
                      customParametersUpdate({
                        templateId,
                        resource: {
                          nodeId: questionId,
                          key: data.id as TTemplateParameterKeys,
                          value: 'reset',
                          language: questionLanguage,
                          isDefault: isDefault ?? false,
                        },
                        eventType: EEventType.ADDED,
                        description: `User reset ${questionId}/${data.id as TTemplateParameterKeys} to original.`,
                      }),
                    );
                  }
                }}
              >
                Reset to original
              </HubertButton>
            </UneditableInfoTooltip>
          ) : null}

          <UneditableInfoTooltip disabled={disabled}>
            <HubertButton
              size={'xs'}
              disabled={disabled}
              variant={'default'}
              onClick={() => {
                if (data) {
                  const textType = data.id as TextType;
                  const isDefault = q[textType].isDefault;
                  const x = [...q[textType].value];
                  x.push({ id: nanoid(), text: undefined });
                  if (templateId && writesTemplateOverrides) {
                    // add to parameters
                    dispatch(
                      customParametersUpdate({
                        templateId,
                        resource: {
                          nodeId: questionId,
                          key: textType,
                          value: x.map((y) => y.text),
                          language: questionLanguage,
                          isDefault,
                        },
                        eventType: EEventType.ADDED,
                        description: `User modified ${textType} to question "${questionId}".`,
                      }),
                    );
                  }
                  dispatch(
                    addText({
                      questionId,
                      textType,
                      value: x,
                    }),
                  );
                }
              }}
            >
              Add {data ? data.id : ''}
            </HubertButton>
          </UneditableInfoTooltip>
        </div>
      ) : null}
    </div>
  );
};

export default TextBubbleDragAndDropHeaderv2;
